import React from "react";
import { ArrowUpRight } from "lucide-react";
import { appUrl } from "@/lib/referral";

const APP_SIGNUP = "https://work.voxiumscript.com";

/**
 * Three primary cards. Each card has the same editorial structure:
 *  - Roman numeral / mark (subtle persona accent)
 *  - Headline (display serif)
 *  - Body (serif)
 *  - "Best for" italic line
 *  - CTA arrow link
 */
const CARDS = [
  {
    mark: "I",
    eyebrow: "Story Development",
    headline: "Pressure-test the idea before you write it.",
    body:
      "Sit with the Mentor — a fair-witness creative partner trained in story craft. Talk through the premise, the protagonist, the central question. Surface the load-bearing problems early, when revision still costs nothing. Whether you're shaping a feature, breaking a pilot, or testing a logline, Story Development is where the work begins.",
    bestFor:
      "Writers who'd rather find structural cracks in the idea than the third draft.",
    cta: "Start a development session",
    testId: "card-story-development",
  },
  {
    mark: "II",
    eyebrow: "Script Coverage",
    headline: "The room reads. The room decides.",
    body: [
      "Upload your script. Get Foundation Mode — a craft-level diagnostic across the fifteen elements that make stories work. Then convene the Council: Reader, Producer, Director, Audience. Each persona reads from their own chair, in their own voice. They don't agree. They don't flatter. You hear the questions before the room asks them.",
      "If the room says yes, your script earns one more read: the Studio Head. The bet-level perspective. The voice that asks not just \u201Cis this ready,\u201D but \u201Cis this the kind of story I'd invest in right now.\u201D",
    ],
    bestFor:
      "Writers ready to know whether the draft works — and what to fix if it doesn't.",
    cta: "Run a coverage",
    testId: "card-script-coverage",
  },
  {
    mark: "III",
    eyebrow: "Short-form Reads",
    headline: "Logline. Pitch. Synopsis. Treatment.",
    body:
      "The pieces of paper that open doors are the hardest to write well. Voxium Script evaluates each short-form document against the craft standards that working professionals apply — clarity in a sentence, stakes that lean a buyer in, structure visible across the prose. Logline, Pitch Document, Synopsis, and Treatment share a single Short-form reads meter. Use it before you submit. Use it before you pitch.",
    bestFor:
      "Writers preparing materials that need to land in under thirty seconds.",
    cta: "Evaluate a document",
    testId: "card-short-form",
  },
];

export const PrimaryCards = () => {
  return (
    <section
      className="bg-cream w-full px-6 sm:px-10 lg:px-16 pb-28 sm:pb-32 lg:pb-40"
      data-testid="primary-cards-section"
    >
      <div className="max-w-[78rem] mx-auto grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-8 xl:gap-12">
        {CARDS.map((card) => (
          <article
            key={card.testId}
            className="group relative flex flex-col h-full bg-cream border-t border-cream-200 pt-10 lg:pt-12"
            data-testid={card.testId}
          >
            {/* Roman numeral mark + eyebrow */}
            <div className="flex items-baseline gap-4 mb-8">
              <span
                className="display-serif text-gold text-3xl leading-none"
                aria-hidden="true"
              >
                {card.mark}
              </span>
              <span className="eyebrow text-nearblack/70">{card.eyebrow}</span>
            </div>

            {/* Headline */}
            <h3 className="display-serif text-nearblack text-2xl sm:text-[1.75rem] leading-[1.15] mb-6">
              {card.headline}
            </h3>

            {/* Body — supports string OR array (multi-paragraph) */}
            {Array.isArray(card.body) ? (
              card.body.map((para, i) => (
                <p
                  key={i}
                  className="font-body text-base leading-relaxed text-nearblack/80 mb-4 last:mb-6"
                >
                  {para}
                </p>
              ))
            ) : (
              <p className="font-body text-base leading-relaxed text-nearblack/80 mb-6">
                {card.body}
              </p>
            )}

            {/* Best for */}
            <p className="font-body italic text-sm text-nearblack/70 mb-8 max-w-[28ch]">
              Best for: {card.bestFor}
            </p>

            {/* CTA */}
            <div className="mt-auto pt-2">
              <a
                href={appUrl(APP_SIGNUP)}
                className="inline-flex items-center gap-2 font-ui text-sm text-nearblack border-b border-nearblack/30 pb-1 tap-extend transition-colors duration-200 hover:text-gold-600 hover:border-gold"
                data-testid={`${card.testId}-cta`}
              >
                {card.cta}
                <ArrowUpRight size={14} strokeWidth={2} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export default PrimaryCards;
